import { useDragAndDrop } from "react-aria-components";
import { useDragAndDropContext } from "./DragAndDropContext";
import {
  activityOccurrenceStatusSchema,
  categorySchema,
  getUniqueId,
  groupStatusSchema,
  isGroup,
} from "@medoma/models/activities";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Text } from "@medoma/components/Text/Text";
import type { INonRoutableEmployeePerformedActivityOccurrenceOrGroup } from "@medoma/models/activities";
import styles from "../ShiftsRow.module.scss";
import { getItemLabel } from "./dragAndDropUtils";

type UseDragAndDropFromShiftLaneParams = {
  occurrenceOrGroupItem: INonRoutableEmployeePerformedActivityOccurrenceOrGroup;
  shiftId: number;
};

export const useDragAndDropFromShiftLane = ({
  occurrenceOrGroupItem,
  shiftId,
}: UseDragAndDropFromShiftLaneParams) => {
  const { startDrag, endDrag } = useDragAndDropContext();

  const isDragAndDropNotAllowedFromShiftLane = isGroup(occurrenceOrGroupItem)
    ? occurrenceOrGroupItem.visitStatus === groupStatusSchema.enum.finished ||
      occurrenceOrGroupItem.visitStatus === groupStatusSchema.enum.cancelled
    : occurrenceOrGroupItem.status ===
      activityOccurrenceStatusSchema.enum.finished;

  return useDragAndDrop({
    isDisabled: isDragAndDropNotAllowedFromShiftLane,
    getItems: (keys) => {
      return [...keys].map(() => ({
        "medoma-item": getUniqueId(occurrenceOrGroupItem),
        "text-label": getItemLabel(occurrenceOrGroupItem),
        category: occurrenceOrGroupItem.category,
      }));
    },
    onDragStart: () => {
      startDrag({
        source: "shift-lane",
        shiftId,
        item: occurrenceOrGroupItem,
      });
    },
    onDragEnd: () => {
      endDrag();
    },
    renderDragPreview(items) {
      const itemBeingDragged = items?.[0];
      if (!itemBeingDragged) {
        return <></>;
      }

      const draggedName = itemBeingDragged["text-label"];
      const parsedCategory = categorySchema.safeParse(
        itemBeingDragged.category,
      );
      const draggedCategory = parsedCategory.success
        ? parsedCategory.data
        : undefined;

      return (
        <div className={styles.dragPreview}>
          {draggedCategory ? (
            <CategoryIcon category={draggedCategory} size="small" />
          ) : null}
          <Text element="p">{draggedName}</Text>
        </div>
      );
    },
  });
};
